import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ProtocolCategory, ProtocolPage, Team, TeamGroup, TeamInvitation, TeamMember } from '@kaba/shared';
import { api } from './api';
import { queryClient } from './board';

export interface TeamDetail {
  team: Team;
  members: TeamMember[];
  groups: TeamGroup[];
  invitations: TeamInvitation[];
  blocks: { userId: string; name: string; email: string; createdAt: string }[];
}

export const teamsKey = ['teams'] as const;
export const teamKey = (id: string) => ['team', id] as const;

export function useTeams(enabled = true) {
  return useQuery({ queryKey: teamsKey, queryFn: () => api.get<Team[]>('/api/teams'), enabled, staleTime: 30_000 });
}

export function useTeam(id: string | null) {
  return useQuery({ queryKey: teamKey(id ?? ''), queryFn: () => api.get<TeamDetail>(`/api/teams/${id}`), enabled: !!id, retry: false });
}

export interface ProtocolFilter {
  cat: ProtocolCategory | '';
  person: string;
  /** 0 = alle */
  days: number;
}

/** Teamprotokoll seitenweise („Mehr laden“) mit Filtern (Paket 4) */
export function useTeamProtocol(id: string, filter: ProtocolFilter, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['team-protocol', id, filter],
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams();
      if (filter.cat) q.set('cat', filter.cat);
      if (filter.person) q.set('person', filter.person);
      if (filter.days) q.set('days', String(filter.days));
      if (pageParam) q.set('cursor', pageParam);
      return api.get<ProtocolPage>(`/api/teams/${id}/protocol?${q}`);
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Beim Filtern die bisherige Liste stehen lassen, bis die neue da ist (kein Springen)
    placeholderData: keepPreviousData,
    enabled,
  });
}

export async function refreshTeam(id: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: teamKey(id) }),
    queryClient.invalidateQueries({ queryKey: teamsKey }),
    queryClient.invalidateQueries({ queryKey: ['team-protocol', id] }),
    queryClient.invalidateQueries({ queryKey: ['boards'] }),
    queryClient.invalidateQueries({ queryKey: ['team-externals', id] }),
    queryClient.invalidateQueries({ queryKey: ['team-ownerless', id] }),
    queryClient.invalidateQueries({ queryKey: ['team-unmanaged', id] }),
    queryClient.invalidateQueries({ queryKey: ['nav'] }),
  ]);
}
